"use client";

import { BrandLockup } from "@/components/brand";
import { ImpromptuAttemptView } from "@/components/impromptu/ImpromptuAttemptView";
import { ImpromptuLanding } from "@/components/impromptu/ImpromptuLanding";
import { ImpromptuReportView } from "@/components/impromptu/ImpromptuReportView";
import type { ImpromptuSettingsProps } from "@/components/impromptu/ImpromptuSettings";
import { ImpromptuSetupPanel } from "@/components/impromptu/ImpromptuSetupPanel";
import { useImpromptuAttempt } from "@/hooks/useImpromptuAttempt";
import { captureImpromptuEvent } from "@/lib/impromptu/analytics";
import {
  IMPROMPTU_LANGUAGES,
  type ImpromptuLanguageCode,
  type ImpromptuLearnerProfile,
  type ImpromptuPromptSelection,
  getImpromptuPrompts,
  isImpromptuGeminiAudioClientEnabled,
  isImpromptuPronunciationClientEnabled,
  resolveImpromptuLanguageCode,
  toImpromptuPromptSelection,
} from "@/lib/impromptu/config";
import { IMPROMPTU_COPY } from "@/lib/impromptu/copy";
import {
  type ImpromptuLanguagePage,
  getImpromptuLanguagePageByCode,
} from "@/lib/impromptu/language-pages";
import { canEncodePronunciationWav } from "@/lib/impromptu/wav";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Impromptu practice, assembled.
 *
 * This component owns only what outlives an attempt — language, level,
 * pronunciation capability, and which prompt is on screen — and hands the attempt
 * itself to `useImpromptuAttempt`. Everything visible is one of four surfaces
 * chosen by the attempt phase.
 *
 * It previously held the whole feature: MediaRecorder wiring, WAV encoding, two
 * fetches, six timers, and four screens' worth of markup in one 1,383-line file
 * with `phase` as a bare string. The failure was not the length so much as what
 * the length hid — a report screen guarded by `phase === "report" && report`
 * because the types allowed a report phase with no report, and a hand-written
 * error parser that inverted the route's own retry flag.
 */

const COPY = IMPROMPTU_COPY.chrome;

type ImpromptuExperienceProps = {
  /** Rendered inside the multi-mode practice shell rather than at its own route. */
  embedded?: boolean;
  initialLanguageCode?: string;
  initialLearnerProfile?: ImpromptuLearnerProfile;
  languagePage?: ImpromptuLanguagePage;
  onBack?: () => void;
};

export function ImpromptuExperience({
  embedded = false,
  initialLanguageCode = "en",
  initialLearnerProfile = "intermediate",
  languagePage,
  onBack,
}: ImpromptuExperienceProps) {
  const router = useRouter();
  const posthog = usePostHog();
  const resolvedInitialLanguage = resolveImpromptuLanguageCode(initialLanguageCode) ?? "en";

  const [languageCode, setLanguageCode] = useState<ImpromptuLanguageCode>(resolvedInitialLanguage);
  const [learnerProfile, setLearnerProfile] =
    useState<ImpromptuLearnerProfile>(initialLearnerProfile);
  const [prompt, setPrompt] = useState<ImpromptuPromptSelection>(() =>
    toImpromptuPromptSelection(getImpromptuPrompts(resolvedInitialLanguage)[0])
  );
  /**
   * Set on mount rather than during render: the probe reads `window`, and the
   * Browser decoding is needed only when server conversion is rolled back.
   * Normal original-only uploads can request Azure/Gemini on Safari without it.
   */
  const [analysisAudioSupported, setAnalysisAudioSupported] = useState(
    process.env.NEXT_PUBLIC_IMPROMPTU_SERVER_CONVERSION_ENABLED !== "false"
  );

  const prompts = useMemo(() => getImpromptuPrompts(languageCode), [languageCode]);
  const languageName =
    IMPROMPTU_LANGUAGES.find((language) => language.code === languageCode)?.name ?? languageCode;

  /**
   * Three separate reasons pronunciation might not be on offer, and all three have
   * to hold. `fluent_native` is excluded because scoring a native speaker's accent
   * against a reference model is the one use of this feature that would be read as
   * a verdict on the speaker rather than help with a language.
   */
  const pronunciationAvailable =
    isImpromptuPronunciationClientEnabled() &&
    analysisAudioSupported &&
    learnerProfile !== "fluent_native";
  const geminiAudioAvailable = isImpromptuGeminiAudioClientEnabled() && analysisAudioSupported;

  const attempt = useImpromptuAttempt({
    languageCode,
    learnerProfile,
    prompt,
    pronunciationRequested: pronunciationAvailable,
    geminiAudioRequested: geminiAudioAvailable,
    posthog,
  });

  const capture = useCallback(
    (event: string, properties: Record<string, unknown>) => {
      captureImpromptuEvent(posthog, event, {
        language_code: languageCode,
        learner_profile: learnerProfile,
        ...properties,
      });
    },
    [languageCode, learnerProfile, posthog]
  );

  useEffect(() => {
    if (!isImpromptuPronunciationClientEnabled() && !isImpromptuGeminiAudioClientEnabled()) {
      return;
    }
    setAnalysisAudioSupported(
      process.env.NEXT_PUBLIC_IMPROMPTU_SERVER_CONVERSION_ENABLED !== "false" ||
        canEncodePronunciationWav()
    );
  }, []);

  // Reported once per mount, not once per language change: the learner chose the
  // mode once, and re-reporting it would make the mode look more popular than it
  // is every time someone switches language.
  const routeSelectionTrackedRef = useRef(false);
  useEffect(() => {
    if (embedded || routeSelectionTrackedRef.current) return;
    routeSelectionTrackedRef.current = true;
    capture("impromptu_mode_selected", { surface: "canonical_route" });
  }, [capture, embedded]);

  useEffect(() => {
    capture("impromptu_prompt_shown", {
      prompt_id: prompt.id,
      prompt_category: prompt.category,
      prompt_source: prompt.source,
    });
  }, [capture, prompt.category, prompt.id, prompt.source]);

  const handleLanguageChange = useCallback(
    (nextLanguageCode: ImpromptuLanguageCode) => {
      setLanguageCode(nextLanguageCode);
      // The prompt has to move with the language: leaving a Dutch prompt on
      // screen after switching to Spanish would send audio for transcription
      // against a language it is not in.
      setPrompt(toImpromptuPromptSelection(getImpromptuPrompts(nextLanguageCode)[0]));
      attempt.reset();
      if (languagePage) {
        const nextPage = getImpromptuLanguagePageByCode(nextLanguageCode);
        if (nextPage) router.push(nextPage.path);
      }
    },
    [attempt, languagePage, router]
  );

  const handleNewPrompt = useCallback(() => {
    const currentIndex = prompts.findIndex((candidate) => candidate.id === prompt.id);
    setPrompt(toImpromptuPromptSelection(prompts[(currentIndex + 1) % prompts.length]));
    attempt.reset();
  }, [attempt, prompt.id, prompts]);

  const handleBack = useCallback(() => {
    attempt.reset();
    onBack?.();
  }, [attempt, onBack]);

  const settings: Omit<ImpromptuSettingsProps, "surface" | "className" | "disabled"> = {
    languageCode,
    learnerProfile,
    onLanguageChange: handleLanguageChange,
    onLearnerProfileChange: setLearnerProfile,
  };

  const { phase } = attempt;
  const attemptSurface = !embedded && phase.kind !== "report" ? "light" : "dark";
  const isLightAttempt = attemptSurface === "light";

  // The marketing surface stands in for the ready state on the public route.
  if (!embedded && phase.kind === "ready") {
    return (
      <ImpromptuLanding
        languageCode={languageCode}
        learnerProfile={learnerProfile}
        prompt={prompt}
        prompts={prompts}
        onLanguageChange={handleLanguageChange}
        onLearnerProfileChange={setLearnerProfile}
        onPromptChange={setPrompt}
        onStart={() => void attempt.startAttempt()}
        languagePage={languagePage}
      />
    );
  }

  return (
    <div
      className={`${embedded ? "flex min-h-0 flex-1 pt-16 md:pt-[72px]" : "min-h-[100dvh]"} ${
        isLightAttempt ? "bg-[#F5F5F0] text-[#1C1C1E]" : "bg-[#0F0F14] text-[#F9F9F9]"
      }`}
    >
      <div
        className={`mx-auto flex w-full max-w-7xl flex-col px-5 py-4 sm:px-8 ${
          embedded ? "min-h-0 flex-1" : "min-h-full"
        }`}
      >
        <header
          className={`flex min-h-16 items-center justify-between border-b pb-4 ${
            isLightAttempt ? "border-[#D1D1D6]" : "border-white/10"
          }`}
        >
          {embedded && onBack ? (
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm font-bold text-[#AEAEB2] transition-colors hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F]"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              {COPY.backToModes}
            </button>
          ) : (
            <Link
              href="/"
              className="inline-flex min-h-11 items-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F]"
              aria-label={COPY.backToHomeLabel}
            >
              <BrandLockup
                surface={attemptSurface}
                className="h-auto w-[132px] sm:w-[168px]"
                priority
              />
            </Link>
          )}
          <span
            className={`text-sm font-bold ${isLightAttempt ? "text-[#A8431F]" : "text-[#AEAEB2]"}`}
          >
            {COPY.surfaceName}
          </span>
        </header>

        <main
          className={`min-w-0 flex-1 ${
            embedded ? "py-5 sm:py-6 lg:py-8" : "py-8 sm:py-12 lg:py-16"
          }`}
        >
          {phase.kind === "ready" ? (
            <ImpromptuSetupPanel
              languageCode={languageCode}
              learnerProfile={learnerProfile}
              prompt={prompt}
              prompts={prompts}
              settings={settings}
              onPromptChange={setPrompt}
              onStart={() => void attempt.startAttempt()}
            />
          ) : phase.kind === "report" ? (
            <ImpromptuReportView
              report={phase.report}
              pronunciation={phase.pronunciation}
              onRetry={() => void attempt.retryPrompt()}
              onNewPrompt={handleNewPrompt}
            />
          ) : (
            <ImpromptuAttemptView
              surface={attemptSurface}
              phase={phase}
              getRecordingStream={attempt.getRecordingStream}
              promptText={prompt.text}
              languageName={languageName}
              settings={settings}
              // Locked from the moment the microphone opens until the attempt is
              // over. Changing the language mid-recording would transcribe the
              // audio against a language it is not in; changing the level would
              // grade it against a rubric the learner did not choose.
              settingsLocked={phase.kind !== "failed"}
              onStop={attempt.stopRecording}
              onCancel={attempt.reset}
              recovery={attempt.recovery}
            />
          )}
        </main>
      </div>
    </div>
  );
}
